import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    ArrowLeft, Building2, MapPin, Briefcase, Loader2, DollarSign, ArrowRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * Public Company page — /companies/{slug}
 *
 * Basic depth per plan: company name, industry, list of open jobs, locations.
 * SEO: Organization JSON-LD + canonical + OG tags so shares render cleanly.
 */
const CompanyProfile = () => {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/companies/${slug}`)
            .then(({ data }) => setData(data))
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
                else toast.error(formatApiError(err));
            })
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <div className="container-page py-24 flex flex-col items-center text-muted-foreground">
                <Loader2 size={22} className="animate-spin mb-3" /> Loading company profile…
            </div>
        );
    }
    if (notFound) {
        return (
            <div className="container-page py-24 text-center">
                <Building2 size={26} className="mx-auto text-emerald-500" />
                <h1 className="mt-4 font-heading font-semibold text-2xl text-foreground">Company not found</h1>
                <p className="mt-2 text-sm text-muted-foreground">This company doesn&apos;t have any live roles on Simonara right now.</p>
                <Link to="/careers" className="btn-green mt-6 inline-flex text-sm">Browse open roles</Link>
            </div>
        );
    }
    if (!data) return null;

    const canonicalUrl = typeof window !== "undefined"
        ? `${window.location.origin}/companies/${data.company_slug || slug}`
        : `/companies/${slug}`;
    const title = `${data.company} — Careers on Simonara`;
    const description = `${data.company} has ${data.job_count} open role${data.job_count === 1 ? "" : "s"} on Simonara. Apply through Simonara for AI-matched, verified hiring.`;
    const ogImage = "https://customer-assets.emergentagent.com/job_simonara-careers/artifacts/myufl7i3_AI-powered%20recruiting%20%26%20HR%20consulting_20260711_112026_0000%20%281%29.png";

    const orgJsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": data.company,
        "url": canonicalUrl,
        "industry": data.industry || undefined,
    };

    return (
        <div data-testid="company-profile-page" className="bg-background">
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />
                <meta name="robots" content="index, follow" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Simonara Talent" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={ogImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={ogImage} />
                <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
            </Helmet>

            {/* Hero */}
            <section className="bg-brand-navy dark:bg-slate-900 text-white pt-16 pb-14">
                <div className="container-page">
                    <Link to="/careers" data-testid="company-back" className="inline-flex items-center text-sm text-white/70 hover:text-brand-green">
                        <ArrowLeft size={14} className="mr-1.5" /> Back to Open Roles
                    </Link>
                    <div className="mt-6 flex items-start gap-5">
                        <div className="hidden sm:flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15 shrink-0 overflow-hidden">
                            {data.logo_url ? (
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL || ""}${data.logo_url}`}
                                    alt={`${data.company} logo`}
                                    data-testid="company-logo"
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <Building2 size={26} className="text-brand-green" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <p className="overline !text-brand-green">Employer profile</p>
                            <h1 data-testid="company-title" className="mt-2 font-heading font-medium text-3xl sm:text-5xl tracking-tight leading-tight">
                                {data.company}
                            </h1>
                            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-white/80 text-sm">
                                {data.industry && <span className="inline-flex items-center gap-1.5"><Briefcase size={14} /> {data.industry}</span>}
                                <span className="inline-flex items-center gap-1.5">
                                    <Building2 size={14} /> {data.job_count} open role{data.job_count === 1 ? "" : "s"}
                                </span>
                                {data.locations?.length > 0 && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin size={14} /> {data.locations.slice(0, 3).join(" · ")}
                                        {data.locations.length > 3 && ` +${data.locations.length - 3}`}
                                    </span>
                                )}
                            </div>
                            {data.culture_blurb && (
                                <p data-testid="company-blurb" className="mt-5 max-w-2xl text-white/85 text-sm leading-relaxed">
                                    {data.culture_blurb}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Jobs list */}
            <section className="container-page py-14">
                <h2 className="font-heading font-semibold text-xl text-foreground">Open roles at {data.company}</h2>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data.jobs || []).map((job) => (
                        <article
                            key={job.id}
                            data-testid={`company-job-${job.id}`}
                            className="card-soft p-6 flex flex-col group hover:border-emerald-500/40 transition-colors"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <h3 className="font-heading font-semibold text-lg text-foreground group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors leading-snug">
                                    <Link to={`/jobs/${job.slug || job.id}`}>{job.title}</Link>
                                </h3>
                                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 border text-[10px]">{job.employment_type}</Badge>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
                                {job.salary && <span className="inline-flex items-center gap-1"><DollarSign size={12} /> {job.salary}</span>}
                                {job.remote_status && job.remote_status !== "on_site" && (
                                    <Badge variant="outline" className="text-[10px] capitalize">{job.remote_status}</Badge>
                                )}
                            </div>
                            <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                                <span className="text-[11px] text-muted-foreground">Posted {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : "recently"}</span>
                                <Link to={`/jobs/${job.slug || job.id}`} data-testid={`company-view-job-${job.id}`} className="text-sm font-semibold text-brand-green dark:text-emerald-400 group-hover:underline inline-flex items-center gap-1">
                                    View & apply <ArrowRight size={13} />
                                </Link>
                            </div>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default CompanyProfile;
